<template lang="pug">
.password-strength-meter(:class="{ mounted: isMounted }")
    .password-strength-meter--fill(:data-score="score")
    .password-strength-label(v-if="label")
        span {{ label }}
</template>

<i18n>
{
    "en": {
        "Very weak": "Very weak",
        "Weak": "Weak",
        "Fair": "Fair",
        "Strong": "Strong",
        "Very strong": "Very strong",
        "zxcvbn:straight-rows-of-keys": "Straight rows of keys are easy to guess",
        "zxcvbn:short-keyboard-patterns": "Short keyboard patterns are easy to guess",
        "zxcvbn:repeats-like-aaa": "Repeats like \"aaa\" are easy to guess",
        "zxcvbn:repeats-like-abcabcabc": "Repeats like \"abcabcabc\" are easy to guess",
        "zxcvbn:sequences-like-abc-or-6543": "Sequences like abc or 6543 are easy to guess",
        "zxcvbn:recent-years": "Recent years are easy to guess",
        "zxcvbn:dates": "Dates are often easy to guess",
        "zxcvbn:top-10-common-password": "This is a top-10 common password",
        "zxcvbn:top-100-common-password": "This is a top-100 common password",
        "zxcvbn:very-common-password": "This is a very common password",
        "zxcvbn:similar-to-commonly-used-password": "This is similar to a commonly used password",
        "zxcvbn:word-by-itself": "A word by itself is easy to guess",
        "zxcvbn:names-and-surnames": "Names and surnames by themselves are easy to guess",
        "zxcvbn:common-names-and-surnames": "Common names and surnames are easy to guess"
    },
    "de": {
        "Very weak": "Sehr schwach",
        "Weak": "Schwach",
        "Fair": "Mittel",
        "Strong": "Stark",
        "Very strong": "Sehr stark",
        "zxcvbn:straight-rows-of-keys": "Gerade Tastenreihen sind leicht zu erraten",
        "zxcvbn:short-keyboard-patterns": "Kurze Tastaturmuster sind leicht zu erraten",
        "zxcvbn:repeats-like-aaa": "Wiederholungen wie \"aaa\" sind leicht zu erraten",
        "zxcvbn:repeats-like-abcabcabc": "Wiederholungen wie \"abcabcabc\" sind leicht zu erraten",
        "zxcvbn:sequences-like-abc-or-6543": "Abfolgen wie abc oder 6543 sind leicht zu erraten",
        "zxcvbn:recent-years": "Die letzten Jahre sind leicht zu erraten",
        "zxcvbn:dates": "Ein Datum ist oft leicht zu erraten",
        "zxcvbn:top-10-common-password": "Dies ist ein Top-10 häufig verwendetes Passwort",
        "zxcvbn:top-100-common-password": "Dies ist ein Top-100 häufig verwendetes Passwort",
        "zxcvbn:very-common-password": "Dies ist ein sehr häufig verwendetes Passwort",
        "zxcvbn:similar-to-commonly-used-password": "Dies ähnelt einem häufig verwendeten Passwort",
        "zxcvbn:word-by-itself": "Ein einzelnes Wort ist leicht zu erraten",
        "zxcvbn:names-and-surnames": "Namen und Nachnamen sind leicht zu erraten",
        "zxcvbn:common-names-and-surnames": "Weit verbreitete Namen und Nachnamen sind leicht zu erraten"
    }
}
</i18n>

<script>
import _ from 'lodash';
import axios from 'axios';

var API_ENDPOINT = process.env.VUE_APP_API_ENDPOINT;

var WARNING_MAP = {
    'Straight rows of keys are easy to guess': 'zxcvbn:straight-rows-of-keys',
    'Short keyboard patterns are easy to guess': 'zxcvbn:short-keyboard-patterns',
    'Repeats like "aaa" are easy to guess': 'zxcvbn:repeats-like-aaa',
    'Repeats like "abcabcabc" are only slightly harder to guess than "abc"': 'zxcvbn:repeats-like-abcabcabc',
    'Sequences like abc or 6543 are easy to guess': 'zxcvbn:sequences-like-abc-or-6543',
    'Recent years are easy to guess': 'zxcvbn:recent-years',
    'Dates are often easy to guess': 'zxcvbn:dates',
    'This is a top-10 common password': 'zxcvbn:top-10-common-password',
    'This is a top-100 common password': 'zxcvbn:top-100-common-password',
    'This is a very common password': 'zxcvbn:very-common-password',
    'This is similar to a commonly used password': 'zxcvbn:similar-to-commonly-used-password',
    'A word by itself is easy to guess': 'zxcvbn:word-by-itself',
    'Names and surnames by themselves are easy to guess': 'zxcvbn:names-and-surnames',
    'Common names and surnames are easy to guess': 'zxcvbn:common-names-and-surnames'
};

var SCORE_LABELS = ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'];

export default {
    name: 'PasswordStrengthMeter',
    props: {
        password: {
            type: String,
            default: ''
        }
    },
    data: function() {
        return {
            isMounted: false,
            score: null,
            feedback: null
        };
    },
    computed: {
        label: function() {
            if (this.score === null || this.score === undefined) return '';
            var scoreLabel = this.$t(SCORE_LABELS[this.score] || '');
            var warning = this.translatedWarning;
            if (warning) return scoreLabel + ' — ' + warning;
            return scoreLabel;
        },
        translatedWarning: function() {
            var rawWarning = _.get(this.feedback, 'warning', '');
            if (!rawWarning) return '';
            var translationKey = WARNING_MAP[rawWarning] || rawWarning;
            return this.$t(translationKey);
        }
    },
    watch: {
        password: _.debounce(function(value) {
            this.evaluate(value);
        }, 150),
        score: function(value) {
            this.$emit('score', value);
        },
        feedback: function(value) {
            this.$emit('feedback', value || {});
        }
    },
    mounted: function() {
        var self = this;
        this.evaluate(this.password).then(function() {
            self.isMounted = true;
        });
    },
    methods: {
        evaluate: function(password) {
            var self = this;
            if (!password) {
                this.score = null;
                this.feedback = null;
                return Promise.resolve();
            }
            return axios.post(API_ENDPOINT + 'zxcvbn', { password: password })
                .then(function(response) {
                    self.score = _.get(response, 'data.score', null);
                    self.feedback = _.get(response, 'data.feedback', null);
                })
                .catch(function() {
                    self.score = null;
                    self.feedback = null;
                });
        }
    }
};
</script>

<style lang="sass" scoped>
.password-strength-meter
    position: relative
    height: 4px
    background: rgba(0, 0, 0, 0.1)
    margin: 8px 0 0
    border-radius: 3px

    &:before, &:after
        content: ''
        height: inherit
        background: transparent
        display: block
        border-color: #fff
        border-style: solid
        border-width: 0 2px
        position: absolute
        width: 20%
        z-index: 10

    &:before
        left: 20%

    &:after
        right: 20%

    &.mounted .password-strength-meter--fill
        transition: width 0.5s ease-in-out, background 0.25s

.password-strength-meter--fill
    background: transparent
    height: inherit
    position: absolute
    width: 0
    border-radius: inherit

    &[data-score='0']
        background: #f56c6c
        width: 20%

    &[data-score='1']
        background: #e67e22
        width: 40%

    &[data-score='2']
        background: #f39c12
        width: 60%

    &[data-score='3']
        background: #27ae60
        width: 80%

    &[data-score='4']
        background: #2ecc71
        width: 100%

.password-strength-label
    margin-top: 6px
    font-size: 12px
    color: #909399
    line-height: 1.3
    position: relative
    top: 10px

    [data-score='0'] ~ &,
    [data-score='1'] ~ &
        color: #f56c6c

    [data-score='2'] ~ &
        color: #f39c12

    [data-score='3'] ~ &,
    [data-score='4'] ~ &
        color: #27ae60
</style>
